'use client';

import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { ArrowLeft, ArrowRight, CalendarPlus, Check, CheckCircle } from '@phosphor-icons/react';
import Button from '@/components/ui/Button';
import { ApiError, apiClient, type AppointmentBooking, type AppointmentSchedule, type AppointmentSlot } from '@/lib/api';
import { getStoredUserRaw, getValidAccessToken } from '@/lib/authStorage';
import { matchesTimeRange, TIME_PRESETS, validateTimeRange, type TimeRange } from './appointmentTimeFilter';
import styles from './AppointmentBookingPanel.module.css';

type AppointmentBookingPanelProps = { ownerId: number; schedule: AppointmentSchedule };
type StoredUser = { id?: number; email?: string | null; display_name?: string | null; first_name?: string | null; last_name?: string | null; username?: string | null };
type FieldName = 'name' | 'email' | 'topic' | 'code';
const PREVIEW_DAYS = 2;
const PREVIEW_SLOTS = 3;

function dateKey(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function dayLabel(day: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('ru-RU', { ...options, timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`));
}

function timeLabel(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('ru-RU', { timeZone: timezone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso));
}

function bookingLabel(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('ru-RU', { timeZone: timezone, day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

function createIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID().replaceAll('-', '');
  return `${Date.now()}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`.slice(0, 64);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function AppointmentBookingPanel(props: AppointmentBookingPanelProps) {
  return <BookingFlow key={props.ownerId} {...props} />;
}

function BookingFlow({ ownerId, schedule }: AppointmentBookingPanelProps) {
  const [step, setStep] = useState<'time' | 'details' | 'success'>('time');
  const [today, setToday] = useState(() => dateKey(new Date(), schedule.timezone));
  const [moreDates, setMoreDates] = useState(false);
  const [expandedDays, setExpandedDays] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState<TimeRange>({ from: '', to: '' });
  const [rangeDraft, setRangeDraft] = useState<TimeRange>({ from: '', to: '' });
  const [rangeAttempted, setRangeAttempted] = useState(false);
  const [slots, setSlots] = useState<AppointmentSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<AppointmentSlot | null>(null);
  const [storedUser, setStoredUser] = useState<StoredUser | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationExpiresAt, setVerificationExpiresAt] = useState(0);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [sendingCode, setSendingCode] = useState(false);
  const [booking, setBooking] = useState(false);
  const [downloadingCalendar, setDownloadingCalendar] = useState(false);
  const [slotsError, setSlotsError] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [warning, setWarning] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [success, setSuccess] = useState<AppointmentBooking | null>(null);
  const [manageToken, setManageToken] = useState('');
  const panelRef = useRef<HTMLElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const rangeInputsRef = useRef<Partial<Record<keyof TimeRange, HTMLInputElement | null>>>({});
  const mountedRef = useRef(false);
  const slotRequestRef = useRef(0);
  const codeRequestRef = useRef(0);
  const selectedSlotRef = useRef<AppointmentSlot | null>(null);
  const bookingPendingRef = useRef(false);
  const successRef = useRef<AppointmentBooking | null>(null);
  const operationRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const uncertainRef = useRef(false);
  const fieldFocusRef = useRef<FieldName | null>(null);
  const previousStepRef = useRef(step);

  const days = useMemo(() => Array.from({ length: Math.min(31, Math.max(1, schedule.booking_horizon_days)) }, (_, offset) => {
    const date = new Date(`${today}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() + offset);
    return date.toISOString().slice(0, 10);
  }), [today, schedule.booking_horizon_days]);
  const firstDay = days[0];
  const lastDay = days[days.length - 1];
  const needsEmailCode = !storedUser?.email;
  const isOwner = ownerId > 0 && Number(storedUser?.id) === ownerId;
  const hasAccountDetails = Boolean(storedUser?.email && name);
  const availableSlots = useMemo(() => slots.filter((slot) => slot.state === 'available'
    && matchesTimeRange(slot.starts_at, schedule.timezone, timeRange)), [slots, timeRange, schedule.timezone]);
  const availableDays = useMemo(() => {
    const grouped = new Map<string, AppointmentSlot[]>();
    for (const slot of [...availableSlots].sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))) {
      const day = dateKey(new Date(slot.starts_at), schedule.timezone);
      if (day < firstDay || day > lastDay) continue;
      const group = grouped.get(day) || [];
      group.push(slot);
      grouped.set(day, group);
    }
    return Array.from(grouped, ([day, intervals]) => ({ day, intervals }));
  }, [availableSlots, schedule.timezone, firstDay, lastDay]);
  const rangeErrors = rangeAttempted ? validateTimeRange(rangeDraft) : {};

  useEffect(() => {
    mountedRef.current = true;
    const syncUser = () => {
      try {
        const raw = getValidAccessToken() ? getStoredUserRaw() : null;
        const user = raw ? JSON.parse(raw) as StoredUser : null;
        setStoredUser(user);
        if (user) {
          setName(user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || '');
          setEmail(user.email || '');
        }
      } catch {
        setStoredUser(null);
      }
    };
    syncUser();
    window.addEventListener('lisad_auth_changed', syncUser);
    return () => {
      window.removeEventListener('lisad_auth_changed', syncUser);
      mountedRef.current = false;
      slotRequestRef.current += 1;
      codeRequestRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (previousStepRef.current !== step) {
      panelRef.current?.focus();
      previousStepRef.current = step;
    }
  }, [step]);

  useEffect(() => {
    if (fieldFocusRef.current) {
      (formRef.current?.elements.namedItem(fieldFocusRef.current) as HTMLElement | null)?.focus();
      fieldFocusRef.current = null;
    }
  }, [fieldErrors]);

  const loadSlots = useCallback(async () => {
    const request = ++slotRequestRef.current;
    setSlotsLoading(true);
    setSlotsError('');
    try {
      const result = await apiClient.getPublicAppointmentSlots(ownerId, firstDay, lastDay);
      if (!mountedRef.current || request !== slotRequestRef.current) return;
      setSlots(result.slots);
      const previous = selectedSlotRef.current;
      if (previous && !uncertainRef.current) {
        const refreshed = result.slots.find((slot) => slot.starts_at === previous.starts_at && slot.state === 'available') || null;
        selectedSlotRef.current = refreshed;
        setSelectedSlot(refreshed);
        if (!refreshed && !successRef.current) {
          operationRef.current = null;
          setStep('time');
          setNotice('Выбранное время больше недоступно. Выберите другой интервал — заполненные данные сохранены.');
        }
      }
    } catch (reason: unknown) {
      if (mountedRef.current && request === slotRequestRef.current) setSlotsError(reason instanceof Error ? reason.message : 'Не удалось обновить свободное время.');
    } finally {
      if (mountedRef.current && request === slotRequestRef.current) setSlotsLoading(false);
    }
  }, [ownerId, firstDay, lastDay, schedule]);

  useEffect(() => { void loadSlots(); }, [loadSlots]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'visible' || bookingPendingRef.current || successRef.current) return;
      const currentDay = dateKey(new Date(), schedule.timezone);
      if (currentDay !== today) {
        setToday(currentDay);
      } else {
        void loadSlots();
      }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [loadSlots, schedule.timezone, today]);

  function selectSlot(slot: AppointmentSlot | null) {
    if (isOwner) return;
    selectedSlotRef.current = slot;
    setSelectedSlot(slot);
    operationRef.current = null;
    uncertainRef.current = false;
    setNotice('');
  }

  function applyTimeRange(value: TimeRange) {
    setRangeAttempted(true);
    const errors = validateTimeRange(value);
    if (Object.keys(errors).length) {
      rangeInputsRef.current[errors.from ? 'from' : 'to']?.focus();
      return;
    }
    setTimeRange(value);
    setRangeDraft(value);
    setRangeAttempted(false);
    if (selectedSlot && !matchesTimeRange(selectedSlot.starts_at, schedule.timezone, value)) selectSlot(null);
  }

  function clearFieldError(field: FieldName) {
    operationRef.current = null;
    uncertainRef.current = false;
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
    setError('');
  }

  async function sendCode() {
    if (sendingCode || isOwner) return;
    const request = ++codeRequestRef.current;
    setSendingCode(true);
    setError('');
    try {
      const result = await apiClient.requestAppointmentGuestCode(email.trim());
      if (!mountedRef.current || request !== codeRequestRef.current) return;
      setVerificationToken(result.verification_token);
      setVerificationCode('');
      setVerificationExpiresAt(Date.now() + result.expires_in * 1000);
      operationRef.current = null;
      uncertainRef.current = false;
      fieldFocusRef.current = 'code';
      setFieldErrors({});
      setNotice('Код отправлен на указанный email. Введите шесть цифр из письма.');
    } catch (reason: unknown) {
      if (mountedRef.current && request === codeRequestRef.current) setError(reason instanceof Error ? reason.message : 'Не удалось отправить код.');
    } finally {
      if (mountedRef.current && request === codeRequestRef.current) setSendingCode(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (bookingPendingRef.current || sendingCode) return;
    if (isOwner) { setError('Вы не можете записаться к себе.'); return; }
    const errors: Partial<Record<FieldName, string>> = {};
    if (!name.trim()) errors.name = 'Укажите ФИО.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errors.email = 'Укажите корректный email.';
    if (!topic.trim()) errors.topic = 'Кратко опишите тему встречи.';
    if (needsEmailCode && verificationToken && !/^\d{6}$/.test(verificationCode)) errors.code = 'Введите шестизначный код из письма.';
    if (Object.keys(errors).length) {
      fieldFocusRef.current = (['name', 'email', 'topic', 'code'] as FieldName[]).find((field) => errors[field]) || null;
      setFieldErrors(errors);
      return;
    }
    if (!selectedSlot || selectedSlot.state !== 'available') {
      setStep('time');
      setNotice('Выберите доступное время.');
      return;
    }
    if (needsEmailCode && !verificationToken) {
      await sendCode();
      return;
    }
    if (needsEmailCode && verificationExpiresAt <= Date.now() && !uncertainRef.current) {
      setVerificationToken('');
      setVerificationCode('');
      setError('Срок действия кода истёк. Получите новый код на email.');
      return;
    }
    const payload = {
      owner_id: ownerId, starts_at: selectedSlot.starts_at, name: name.trim(), email: email.trim(), topic: topic.trim(),
      verification_token: verificationToken || undefined, verification_code: verificationCode || undefined,
    };
    const fingerprint = JSON.stringify(payload);
    if (operationRef.current?.fingerprint !== fingerprint) operationRef.current = { fingerprint, key: createIdempotencyKey() };
    bookingPendingRef.current = true;
    slotRequestRef.current += 1;
    setSlotsLoading(false);
    setBooking(true);
    setError('');
    setNotice('');
    setWarning('');
    try {
      const result = await apiClient.createAppointment({ ...payload, idempotency_key: operationRef.current.key });
      if (!mountedRef.current) return;
      uncertainRef.current = false;
      successRef.current = result.booking;
      setSuccess(result.booking);
      setManageToken(result.manage_token || '');
      setWarning(result.warnings?.join('. ') || '');
      setStep('success');
    } catch (reason: unknown) {
      if (!mountedRef.current) return;
      setError(reason instanceof Error ? reason.message : 'Не удалось подтвердить запись. Повторите попытку.');
      // После неопределённого ответа сохраняем ключ и слот: сервер мог уже создать встречу.
      uncertainRef.current = !(reason instanceof ApiError) || !reason.status || reason.status >= 500;
      if (uncertainRef.current) setNotice('Ответ сервера не получен. Повторите подтверждение без изменения данных — уже созданная запись не продублируется.');
      if (reason instanceof ApiError && reason.status === 409) {
        operationRef.current = null;
        await loadSlots();
      }
    } finally {
      bookingPendingRef.current = false;
      if (mountedRef.current) setBooking(false);
    }
  }

  async function handleCalendar() {
    if (!success || downloadingCalendar) return;
    setDownloadingCalendar(true);
    setError('');
    try {
      const blob = manageToken ? await apiClient.downloadGuestAppointmentCalendar(manageToken) : await apiClient.downloadAppointmentCalendar(success.id);
      if (mountedRef.current) downloadBlob(blob, `lisad-meeting-${success.id}.ics`);
    } catch (reason: unknown) {
      if (mountedRef.current) setError(reason instanceof Error ? reason.message : 'Не удалось скачать календарь.');
    } finally {
      if (mountedRef.current) setDownloadingCalendar(false);
    }
  }

  const summary = selectedSlot ? `${bookingLabel(selectedSlot.starts_at, schedule.timezone)}–${timeLabel(selectedSlot.ends_at, schedule.timezone)}` : 'Выберите удобное время';
  const meetingMode = success?.meeting_mode || selectedSlot?.meeting_mode || schedule.default_meeting_mode;
  const location = success?.location_text || schedule.offline_location;
  const timezoneLabel = schedule.timezone === 'Europe/Moscow' ? 'время по Москве' : schedule.timezone;
  const datesToggle = <button type="button" className={styles.textButton} aria-expanded={moreDates} aria-controls="appointment-more-dates" onClick={() => setMoreDates(!moreDates)}>{moreDates ? 'Свернуть' : 'Другие даты'} <ArrowRight size={18} aria-hidden="true" /></button>;

  function renderDay({ day, intervals }: typeof availableDays[number], withToggle = false) {
    const expanded = expandedDays.includes(day);
    const label = dayLabel(day, { weekday: 'long', day: 'numeric', month: 'long' });
    return <div className={styles.dayRow} key={day}>
      <h3 id={`appointment-day-${day}`} className={styles.dayLabel}>{label}</h3>
      <div id={`appointment-slots-${day}`} className={styles.slots} role="group" aria-labelledby={`appointment-day-${day}`}>
        {(expanded ? intervals : intervals.slice(0, PREVIEW_SLOTS)).map((slot) => {
          const selected = selectedSlot?.starts_at === slot.starts_at;
          return <button type="button" key={slot.starts_at} className={`${styles.slot} ${selected ? styles.selected : ''}`} aria-pressed={selected} aria-label={`${label}, ${timeLabel(slot.starts_at, schedule.timezone)}–${timeLabel(slot.ends_at, schedule.timezone)}, ${slot.meeting_mode === 'online' ? 'онлайн' : 'очно'}, ${timezoneLabel}`} onClick={() => selectSlot(slot)}>
            {timeLabel(slot.starts_at, schedule.timezone)}<Check size={16} weight="bold" aria-hidden="true" className={selected ? undefined : styles.unselectedCheck} />
          </button>;
        })}
        {intervals.length > PREVIEW_SLOTS && <button type="button" className={styles.textButton} aria-expanded={expanded} aria-controls={`appointment-slots-${day}`} aria-label={`${expanded ? 'Свернуть время' : 'Ещё время'}: ${label}`} onClick={() => setExpandedDays((current) => expanded ? current.filter((value) => value !== day) : [...current, day])}>{expanded ? 'Свернуть время' : 'Ещё время'}</button>}
      </div>
      {withToggle && <div className={styles.datesToggle}>{datesToggle}</div>}
    </div>;
  }

  return (
    <section id="meeting" ref={panelRef} tabIndex={-1} className={styles.panel} aria-labelledby="booking-title" data-testid="public-appointment-booking" data-booking-step={step}>
      <div className={styles.info}>
        <h2 id="booking-title">{isOwner ? 'Ваши часы приёма' : step === 'success' ? 'Запись подтверждена' : step === 'details' ? 'Детали встречи' : 'Записаться на встречу'}</h2>
        <p className={styles.metadata}>
          <span>{meetingMode === 'online' ? 'Онлайн' : 'Очно'} · {schedule.slot_duration_minutes} минут</span>
          {location && meetingMode === 'offline' && <span> · <span className={styles.locationLabel}>Место: </span>{location}</span>}
          {step !== 'time' && <span> · {timezoneLabel}</span>}
        </p>
      </div>

      {isOwner && <div className={styles.ownerNotice}>
        <p>Вы не можете записаться к себе.</p>
        <Link className={styles.secondaryAction} href="/profile/appointments">Управлять приёмом <ArrowRight size={18} aria-hidden="true" /></Link>
      </div>}

      {!isOwner && step === 'time' && <>
        <div className={styles.picker} aria-busy={slotsLoading}>
          <div className={styles.availabilityHeading}><span>День встречи</span><span>Начало · {timezoneLabel}</span></div>
          {slotsLoading ? <p className={styles.empty} role="status">Обновляем свободное время…</p> : slotsError ? null : availableDays.length ? availableDays.slice(0, PREVIEW_DAYS).map((group, index, preview) => renderDay(group, index === preview.length - 1)) : <div className={styles.emptyState}>
            <p className={styles.empty}>{timeRange.from || timeRange.to ? 'В этом диапазоне нет свободного времени. Измените или сбросьте фильтр.' : 'Пока нет свободного времени для записи. Попробуйте зайти позже.'}</p>
            {datesToggle}
          </div>}
          <div id="appointment-more-dates" className={styles.moreDates} hidden={!moreDates}>
          <div className={styles.filters} role="group" aria-label="Подходящее время дня">
            {TIME_PRESETS.map((value) => <button key={value.id} type="button" className={timeRange.from === value.range.from && timeRange.to === value.range.to ? styles.active : ''} aria-pressed={timeRange.from === value.range.from && timeRange.to === value.range.to} onClick={() => applyTimeRange(value.range)}>{value.label}</button>)}
          </div>
          <details className={styles.rangeDisclosure}>
            <summary>Другой диапазон времени</summary>
            <p id="appointment-range-help" className={styles.micro}>Время начала встречи: «с» включительно, «до» — не включая. Часовой пояс: {schedule.timezone}. Пустое поле — без ограничения.</p>
            <div className={styles.rangeFields}>
              {(['from', 'to'] as const).map((field) => <label key={field} htmlFor={`appointment-range-${field}`}>{field === 'from' ? 'С' : 'До'}
                <input ref={(node) => { rangeInputsRef.current[field] = node; }} id={`appointment-range-${field}`} type="text" placeholder="ЧЧ:ММ" maxLength={5} value={rangeDraft[field]} aria-describedby={`appointment-range-help${rangeErrors[field] ? ` appointment-range-${field}-error` : ''}`} aria-invalid={Boolean(rangeErrors[field])} onChange={(event) => setRangeDraft((previous) => ({ ...previous, [field]: event.target.value }))} />
                {rangeErrors[field] && <span id={`appointment-range-${field}-error`} className={styles.fieldError}>{rangeErrors[field]}</span>}
              </label>)}
            </div>
            {Object.keys(rangeErrors).length > 0 && <p className={styles.fieldError} role="alert">Диапазон не применён. Ранее выбранные фильтр и время сохранены.</p>}
            <button className={styles.textButton} type="button" onClick={() => applyTimeRange(rangeDraft)}>Применить диапазон</button>
          </details>
          {(timeRange.from || timeRange.to) && <p className={styles.rangeStatus} role="status">Начало: {timeRange.from ? `с ${timeRange.from}` : 'в любое время'}{timeRange.to ? ` до ${timeRange.to}` : ''} <button type="button" className={styles.textButton} onClick={() => applyTimeRange({ from: '', to: '' })}>Сбросить</button></p>}
            {!slotsLoading && !slotsError && <div className={styles.additionalDays}>{availableDays.slice(PREVIEW_DAYS).map((group) => renderDay(group))}</div>}
            {!slotsLoading && !slotsError && availableDays.length <= PREVIEW_DAYS && <p className={styles.micro}>Других свободных дней до {dayLabel(lastDay, { day: 'numeric', month: 'long' })} нет.</p>}
          </div>
        </div>
        <div className={styles.summary}>
          <p className={styles.selectedSummary} aria-live="polite" aria-atomic="true">{selectedSlot && <span className={styles.summaryLabel}>Выбрано: </span>}{summary}</p>
          <Button className={styles.primary} disabled={!selectedSlot || slotsLoading || Boolean(slotsError)} onClick={() => { setError(''); setNotice(''); setStep('details'); }}>К деталям встречи <ArrowRight size={20} aria-hidden="true" /></Button>
        </div>
      </>}

      {!isOwner && step === 'details' && <form ref={formRef} className={styles.form} noValidate onSubmit={handleSubmit}>
        <div className={styles.formHeading}><p className={styles.selectedSummary}>{summary}</p><button className={styles.secondaryAction} type="button" disabled={booking || sendingCode} onClick={() => setStep('time')}><ArrowLeft size={16} aria-hidden="true" /> Изменить время</button></div>
        <fieldset disabled={booking || sendingCode} className={styles.fields}>
          <legend className="sr-only">Данные посетителя</legend>
          <div className={styles.formGrid} data-account={hasAccountDetails}>
            {hasAccountDetails ? <div className={styles.identity}><span>Вы записываетесь как</span><strong>{name}</strong><span>{email}</span></div> : <>
            <label htmlFor="appointment-name">ФИО<input id="appointment-name" name="name" value={name} disabled={Boolean(storedUser)} autoComplete="name" maxLength={200} aria-required="true" aria-invalid={Boolean(fieldErrors.name)} aria-describedby={fieldErrors.name ? 'appointment-name-error' : undefined} onChange={(event) => { setName(event.target.value); clearFieldError('name'); }} />{fieldErrors.name && <span className={styles.fieldError} id="appointment-name-error">{fieldErrors.name}</span>}</label>
            <label htmlFor="appointment-email">Email<input id="appointment-email" name="email" type="email" autoComplete="email" value={email} disabled={Boolean(storedUser?.email)} maxLength={255} aria-required="true" aria-invalid={Boolean(fieldErrors.email)} aria-describedby={fieldErrors.email ? 'appointment-email-error' : undefined} onChange={(event) => { setEmail(event.target.value); setVerificationToken(''); setVerificationCode(''); codeRequestRef.current += 1; clearFieldError('email'); }} />{fieldErrors.email && <span className={styles.fieldError} id="appointment-email-error">{fieldErrors.email}</span>}</label>
            </>}
            <label htmlFor="appointment-topic">Тема встречи<textarea id="appointment-topic" name="topic" value={topic} rows={1} maxLength={500} placeholder="Кратко опишите вопрос" aria-required="true" aria-invalid={Boolean(fieldErrors.topic)} aria-describedby={fieldErrors.topic ? 'appointment-topic-error' : undefined} onChange={(event) => { setTopic(event.target.value); clearFieldError('topic'); }} />{fieldErrors.topic && <span className={styles.fieldError} id="appointment-topic-error">{fieldErrors.topic}</span>}</label>
          </div>
          {needsEmailCode && verificationToken && <div className={styles.codeArea}><label htmlFor="appointment-code">Код из письма<input id="appointment-code" name="code" value={verificationCode} inputMode="numeric" autoComplete="one-time-code" maxLength={6} aria-required="true" aria-invalid={Boolean(fieldErrors.code)} aria-describedby="appointment-code-help" onChange={(event) => { setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6)); clearFieldError('code'); }} /><span id="appointment-code-help" className={fieldErrors.code ? styles.fieldError : styles.micro}>{fieldErrors.code || 'Шесть цифр. Код действует пять минут.'}</span></label><button className={styles.textButton} type="button" onClick={() => void sendCode()}>Отправить код повторно</button></div>}
        </fieldset>
        <div className={styles.formActions}>
          {needsEmailCode && !verificationToken && <p className={styles.micro}>Подтвердим email кодом из письма.</p>}
          <Button className={styles.primary} type="submit" loading={booking || sendingCode} disabled={!selectedSlot}>{needsEmailCode && !verificationToken ? 'Получить код на email' : 'Подтвердить запись'} <ArrowRight size={18} aria-hidden="true" /></Button>
        </div>
      </form>}

      {!isOwner && step === 'success' && success && <div className={styles.complete}>
        <div className={styles.completeSummary}><CheckCircle size={28} weight="duotone" aria-hidden="true" /><p className={styles.selectedSummary}>{bookingLabel(success.starts_at, success.timezone)}–{timeLabel(success.ends_at, success.timezone)}</p></div>
        {warning && <p role="status" className={styles.notice}>{warning}. Сохраните встречу в календарь.</p>}
        <div className={styles.completeActions}>
          {success.meeting_mode === 'online' && success.online_url && <a className={styles.secondaryAction} href={success.online_url} target="_blank" rel="noopener noreferrer">Открыть онлайн-встречу <ArrowRight size={18} aria-hidden="true" /></a>}
          <Button variant="outline" className={styles.secondaryAction} loading={downloadingCalendar} onClick={handleCalendar}><CalendarPlus size={18} aria-hidden="true" />Добавить в календарь (.ics)</Button>
          {manageToken ? <Link className={styles.secondaryAction} href={`/meet/manage#token=${encodeURIComponent(manageToken)}`}>Управлять записью <ArrowRight size={18} aria-hidden="true" /></Link> : <Link className={styles.secondaryAction} href="/profile/appointments">Мои записи <ArrowRight size={18} aria-hidden="true" /></Link>}
        </div>
      </div>}
      {!isOwner && slotsError && step !== 'success' && <div className={styles.error} role="alert">{slotsError} <button className={styles.textButton} type="button" disabled={slotsLoading} onClick={() => void loadSlots()}>Обновить интервалы</button></div>}
      {!isOwner && error && <p role="alert" className={styles.error}>{error}</p>}
      {!isOwner && <p className={styles.notice} role="status" aria-atomic="true">{notice}</p>}
    </section>
  );
}
